import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { LoginForm } from '../LoginForm/loginForm';
import { SignupForm } from '../SignupForm/signupForm';
import { ForgetPasswordForm } from '../ForgotPasswordForm/forgotPasswordForm';
import { OTPVerificationForm } from '../OTPVerificationForm/otpVerificationForm';
import { useTranslation } from 'react-i18next';

type AuthMode = 'login' | 'signup' | 'forgot-password' | 'otp-verification';

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: AuthMode;
  onSuccess?: () => void;
}

export function AuthDialog({ isOpen, onClose, initialMode = 'login', onSuccess }: AuthDialogProps) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [signupStep, setSignupStep] = useState(1);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);

  const { t } = useTranslation();

  // Check URL parameters and update mode accordingly
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const otpFromUrl = urlParams.get('otp');
    const emailFromUrl = urlParams.get('email');

    if (otpFromUrl && emailFromUrl && isOpen) {
      // If we have both OTP and email parameters, use the initial mode directly
      setMode(initialMode);
    } else {
      setMode(initialMode);
    }
  }, [initialMode, isOpen]);

  const handleClose = useCallback(() => {
    // Clear URL parameters when closing the dialog
    const url = new URL(window.location.href);
    if (
      url.searchParams.has('otp') ||
      url.searchParams.has('email') ||
      url.searchParams.has('pg')
    ) {
      url.searchParams.delete('otp');
      url.searchParams.delete('email');
      url.searchParams.delete('pg');
      window.history.replaceState({}, '', url.pathname + (url.search || ''));
    }
    onClose();
  }, [onClose]);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const handleModeChange = (newMode: AuthMode) => {
    setMode(newMode);
  };

  const renderContent = () => {
    switch (mode) {
      case 'login':
        return (
          <div className="space-y-4 py-4">
            <LoginForm
              onSuccess={() => {
                onSuccess?.();
                onClose();
              }}
            />
            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <button
                  onClick={() => handleModeChange('signup')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Sign up
                </button>
              </p>
              <p className="text-sm text-gray-600">
                <button
                  onClick={() => handleModeChange('forgot-password')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Forgot your password?
                </button>
              </p>
            </div>
          </div>
        );
      case 'signup':
        return (
          <div className="space-y-4 py-4">
            <SignupForm
              onSuccess={() => {
                onSuccess?.();
                onClose();
              }}
              onStepChange={setSignupStep}
            />
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={() => handleModeChange('login')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Sign in
                </button>
              </p>
            </div>
          </div>
        );
      case 'forgot-password':
        return (
          <div className="space-y-4 py-4">
            <ForgetPasswordForm
              onSuccess={() => {
                onSuccess?.();
                onClose();
              }}
              onStepChange={setForgotPasswordStep}
            />
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Remember your password?{' '}
                <button
                  onClick={() => handleModeChange('login')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Back to login
                </button>
              </p>
            </div>
          </div>
        );
      case 'otp-verification':
        return (
          <div className="space-y-4 py-4">
            <OTPVerificationForm
              onSuccess={() => {
                onSuccess?.();
                onClose();
              }}
            />
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Remember your password?{' '}
                <button
                  onClick={() => handleModeChange('login')}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Back to login
                </button>
              </p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackgroundClick}
    >
      <div className="bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl rounded-2xl p-4 sm:p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-800">
              {mode === 'login' && t('pages.welcome_back')}
              {mode === 'signup' && t('pages.create_account')}
              {mode === 'forgot-password' && t('reset_password')}
              {mode === 'otp-verification' && 'Verify Email'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {mode === 'login' && t('pages.login_description')}
              {mode === 'signup' &&
                (signupStep === 1
                  ? t('enter_your_details')
                  : signupStep === 2
                    ? t('set_up_your_password')
                    : signupStep === 3
                      ? t('verify_your_email_address')
                      : t('pages.sign_up_description'))}
              {mode === 'forgot-password' &&
                (forgotPasswordStep === 1
                  ? t('enter_your_email_address')
                  : forgotPasswordStep === 2
                    ? t('verify_your_email_address')
                    : forgotPasswordStep === 3
                      ? t('create_new_password')
                      : t('pages.reset_password_description'))}
              {mode === 'otp-verification' && 'Enter the verification code sent to your email'}
            </p>
          </div>

          <button
            onClick={handleClose}
            aria-label="Close dialog"
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors ml-4 flex-shrink-0"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-2 bg-white rounded-md">{renderContent()}</div>
      </div>
    </div>,
    document.body,
  );
}

export default AuthDialog;
